import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Cpu, 
  Code, 
  Microscope, 
  Calculator,
  Star,
  Clock,
  Users,
  Play,
  BookOpen,
  Award,
  GraduationCap,
  Church,
  Building2,
  CheckCircle
} from 'lucide-react'

const ProgramsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [expandedProgram, setExpandedProgram] = useState<number | null>(null)

  const categories = [
    { id: 'all', name: 'All Programs', icon: BookOpen },
    { id: 'level0', name: 'Cosmos Juniors', icon: Cpu },
    { id: 'level1', name: 'Titans', icon: Cpu },
    { id: 'level2', name: 'Warriors', icon: Code },
    { id: 'level3', name: 'Lakers', icon: Microscope },
    { id: 'online', name: 'Do It Yourself Kits', icon: Calculator },
    { id: 'programs', name: 'Special Programs', icon: Calculator },
    { id: 'workshops', name: 'STEM Workshops', icon: Award },
    { id: 'schools', name: 'School Partnerships', icon: GraduationCap },
    { id: 'communities', name: 'Community Partnerships', icon: Church },
    { id: 'industry', name: 'Industry Visits', icon: Building2 },
  ]

  const programs = [
    // Level 0 - Cosmos Juniors
    {
      id: 0,
      title: 'Cosmos Juniors',
      category: 'level0',
      difficulty: 'Beginner',
      duration: '8 sessions',
      students: 20,
      rating: 4.8,
      price: 'Starter Program',
      image: '/videos/cosmos-cover.mp4',
      description: 'Basic introduction of technology through playful, creative, hands-on activities. Designed to build curiosity and confidence while blending fun unplugged learning with light on-screen exploration.',
      features: [
        'Creative tech exploration',
        'Fun unplugged + light on-screen activities',
        'Build curiosity and confidence'
      ],
      instructor: 'Tech Instructors',
      badge: 'Cosmos',
      ageRange: 'Ages 6-9',
      detailedDescription: 'Cosmos Juniors introduces young learners to the world of technology with simple, playful activities that encourage exploration and creativity. Kids tinker, build, and imagine while learning basic tech concepts.',
      curriculum: [
        { module: 'Discover Tech Around Us', theme: 'Technology in everyday life', topics: ['Patterns & logic games', 'What is a computer?', 'Input/Output basics'] },
        { module: 'Create & Play', theme: 'Hands-on mini projects', topics: ['Art + tech crafts', 'Build a simple sequence', 'Team challenges'] }
      ],
      outcomes: [
        'Spark curiosity and confidence',
        'Understand very basic tech ideas',
        'Enjoy learning through play'
      ]
    },
    // Level 1 - Titans
    {
      id: 1,
      title: 'Titans',
      category: 'level1',
      difficulty: 'All Levels',
      duration: '10 sessions/week',
      students: 30,
      rating: 4.9,
      price: 'Save 20%',
      image: '/videos/titans-cover.mp4',
      description: 'A fun and engaging foundation program introducing kids to AI, coding, web development, and cloud technology through interactive projects and games. Perfect for young tech enthusiasts beginning their STEM journey!',
      features: [
        'AI & Machine Learning',
        'Python Programming', 
        'JavaScript & Web App Development',
    
        'Hands-on projects & real applications',
        'Expert mentorship throughout',
        'Portfolio development',
        'Interactive learning modules'
      ],
      instructor: 'Advanced Tech Specialists',
      badge: 'Titan',
      ageRange: 'Ages 9-15',
      detailedDescription: 'The Titans program makes technology fun and accessible for kids! Through games, stories, and hands-on projects, students will discover how AI works in everyday life, learn to code their own games and apps, build interactive websites, and explore the magic of cloud computing. This program sparks curiosity and builds confidence in young tech enthusiasts.',
      curriculum: [
        {
          module: '🤖 AI & Machine Learning',
          theme: 'Can machines think like us?',
          topics: ['What is AI? (Everyday examples)', 'How machines learn from examples', 'Recognizing pictures and sounds', 'Building a Smart Classifier', 'AI ethics in stories', 'Teachable Machine activities']
        },
        {
          module: '🐍 Python Programming',
          theme: 'Code your imagination into life!',
          topics: ['Getting started with coding', 'Loops & logic games', 'Lists and variables', 'Build a Game (Snake/Tic Tac Toe)', 'Fun with APIs', 'Make a Calculator or Diary App']
        },
        {
          module: '💻 JavaScript & Web Apps',
          theme: 'Create your own website and make it talk!',
          topics: ['Build your first webpage', 'Add interactivity & animations', 'Build a Click Counter Game', 'Learn about APIs', 'Create a Mini Portfolio', 'Publish it online']
        },
        {
          module: '☁️ Cloud & Future Tech',
          theme: 'Where does the internet live?',
          topics: ['What is the Cloud?', 'Host your own mini website', 'Build a Save My Name app', 'Internet Safety & Passwords', 'How apps use the cloud', 'Future of AI & Robotics']
        }
      ],
      outcomes: [
        'Learn to code fun games and apps with Python',
        'Create colorful, interactive websites',
        'Understand how AI works in everyday life',
        'Build and share projects online',
        'Develop creative problem-solving skills',
        'Gain confidence with technology',
        'Have fun while learning important tech skills'
      ]
    },
    // Level 2 - Warriors
    {
      id: 2,
      title: 'Warriors',
      category: 'level2',
      difficulty: 'All Levels',
      duration: '15+ sessions/week',
      students: 50,
      rating: 4.9,
      price: 'Save 20%',
      image: '/videos/warriors-cover.mp4',
      description: 'Hands-on engineering and robotics program where kids learn to build circuits, create robots, and control them with code. Discover how gadgets work, wire up your own projects, and bring robots to life!',
      features: [
        'Electronics and Circuits',
        'Robotics & IoT',
        'Smart robot development',
        'Circuit design & prototyping',
        'Sensors & microcontrollers',
        'Real-world projects',
        'Hands-on building',
        'Fun engineering challenges'
      ],
      instructor: 'Advanced Tech Specialists',
      badge: 'Warrior',
      ageRange: 'Ages 9-15',
      detailedDescription: 'The Warriors program brings technology to life! Kids will discover how everyday gadgets work, build their own circuits and robots, and learn to control them with code. Students will wire up circuits, assemble robots, and make them move, think, and respond using Wi-Fi and Bluetooth. This hands-on program makes electronics and robotics fun and accessible for young engineers!',
      curriculum: [
        {
          module: 'Electronics & Circuits',
          theme: 'Learn how gadgets come to life!',
          topics: ['What\'s inside everyday electronics', 'Building simple light and buzzer circuits', 'Understanding batteries and power flow', 'Using sensors to detect sound, light, or motion', 'Intro to microcontrollers and Arduino', 'Design your own mini circuit board project']
        },
        {
          module: 'Robotics & IoT',
          theme: 'Build your own robot and make it think!',
          topics: ['Assembling and wiring a basic robot', 'Making robots move and follow paths', 'Adding sensors for obstacle detection', 'Controlling robots with Wi-Fi or Bluetooth', 'Voice-controlled or app-controlled robot challenge', 'Hands-on robot & smart systems building']
        },
      ],
      outcomes: [
        'Build electronic circuits from scratch',
        'Program and control robots',
        'Create smart IoT solutions',
        'Wire up sensors and control systems',
        'Make robots move, think, and respond',
        'Bring technology to life through hands-on projects',
        'Gain confidence with electronics and robotics'
      ]
    },
    // Level 3 - Lakers
    {
      id: 3,
      title: 'Lakers',
      category: 'level3',
      difficulty: 'All Levels',
      duration: '15+ sessions/week',
      students: 40,
      rating: 4.9,
      price: 'Save 20%',
      image: '/videos/lakers-cover.mp4',
      description: 'Advanced program where kids explore data science, learn how computer chips work, and create amazing AR/VR worlds! Turn numbers into stories, build with chips, and step into virtual reality.',
      features: [
        'Data Science',
        'Chip Design',
        'Spatial Computing (AR/VR)',
        'Advanced data visualization',
        'Semiconductor technology',
        '3D modeling & immersive experiences',
        'Research projects',
        'Industry mentorship'
      ],
      instructor: 'Advanced Tech Specialists',
      badge: 'Laker',
      ageRange: 'Ages 9-15',
      detailedDescription: 'The Lakers program takes kids on an exciting journey into the future of technology! Students will learn to turn data into stories with colorful charts, discover how computer chips work inside their phones and robots, and create amazing virtual worlds with AR and VR and work on Drone Technology. This program makes advanced technology accessible and fun for curious young minds!',
      curriculum: [
        {
          module: '📊 Data Science',
          theme: 'Turn numbers into stories!',
          topics: ['What is data and where does it come from', 'Collecting and cleaning data', 'Finding patterns in data', 'Creating colorful charts and dashboards', 'Understanding how computers make predictions', 'Intro to AI and machine learning', 'Build your own Data Story project']
        },
        {
          module: '⚙️ Chip Design',
          theme: 'How do computers think?',
          topics: ['Learn how computer chips work inside your phone or robot', 'Build simple logic circuits using lights and switches', 'Program small chips to do tasks', 'Explore how signals travel inside a chip', 'Design simple games using circuit boards', 'Learn how engineers test chips', 'Create your own mini processor or sensor project']
        },
        {
          module: '🕶️ Spatial Computing (AR/VR)',
          theme: 'Step into your own virtual world!',
          topics: ['Learn 3D design and modeling', 'Explore Augmented Reality (AR)', 'Dive into Virtual Reality (VR)', 'Add sounds, effects, and motion', 'Test your projects using VR headsets', 'Build your own VR mini-game or interactive scene']
        }
      ],
      outcomes: [
        'Turn data into stories with colorful charts',
        'Understand how computer chips work',
        'Build logic circuits and sensors',
        'Create amazing AR and VR worlds',
        'Design your own 3D characters and scenes',
        'Explore the future of technology',
        'Develop advanced problem-solving skills'
      ]
    },
    // Do It Yourself Kits Program
    {
      id: 4,
      title: 'Do It Yourself Kits',
      category: 'online',
      difficulty: 'All Levels',
      duration: 'Self-paced',
      students: 60,
      rating: 4.9,
      price: 'Order Kit Today',
      image: '/videos/diy-kits-cover.mp4',
      description: 'Hands-on learning that continues beyond the makerspace. Each kit comes with step-by-step guides and all materials needed for exciting projects.',
      features: [
        'Marine Robot Kits',
        'Solar Street Light Projects',
        'Walking & Talking Robots',
        'Online instructor sessions',
        'Step-by-step guides',
        'All materials included',
        'Flexible scheduling',
        'Progress tracking'
      ],
      instructor: 'Remote Instructors',
      badge: 'DIY Kits'
    },
    // Special Programs
    {
      id: 5,
      title: 'Tech Entrepreneurship for Kids',
      category: 'programs',
      difficulty: 'All Levels',
      duration: '10 weeks',
      students: 43,
      rating: 4.8,
      price: 'Save 20%',
      image: '/videos/techentrepreneurship-cover.mp4',
      description: 'From idea to invention and product launching - nurturing future CEOs. Kids turn ideas into real products with industry expert mentorship.',
      features: [
        'Industry Expert Mentorship',
        'Real-world problem solving',
        'Hackathons & innovation fairs',
        'Product development lifecycle',
        'Business model creation',
        'Pitch presentation skills',
        'Market research',
        'Product launching'
      ],
      instructor: 'CEO Mentors',
      badge: 'Special',
      detailedDescription: 'A unique add-on program that helps kids turn ideas into real products through brainstorming, prototyping, and launching their creations with guided mentorship from industry experts. Kids identify real-world problems, design smart solutions, and showcase their work at hackathons and innovation fairs. To make the experience even more meaningful, children will perform idea-based skits, songs, or short presentations that raise awareness about the social issues they\'re solving, with every performance approved by parents for comfort and safety. This program is designed to cultivate an entrepreneurial mindset, a crucial skill in today\'s world. Through mentorship, teamwork, and fun pitch sessions, kids gain confidence, leadership, and the belief that their ideas can truly change the world.',
      ageRange: 'Ages 9-18'
    },
    // STEM Workshops
    {
      id: 6,
      title: 'STEM Workshops',
      category: 'workshops',
      difficulty: 'All Levels',
      duration: 'Flexible',
      students: 30,
      rating: 4.9,
      price: 'Contact Us',
      image: '/videos/stem-workshops-cover.mp4',
      description: 'We are coming to your community spaces and schools. Are you ready? Bring hands-on STEM learning directly to your location with our expert instructors.',
      features: [
        'Community center workshops',
        'School program integration',
        'Flexible scheduling',
        'All materials provided',
        'Expert instructors on-site',
        'Customized curriculum',
        'Group learning experiences',
        'Local community engagement'
      ],
      instructor: 'Community STEM Team',
      badge: 'Workshop'
    },
    // School Partnerships
    {
      id: 7,
      title: 'School Partnerships',
      category: 'schools',
      difficulty: 'All Levels',
      duration: 'Flexible',
      students: 800,
      rating: 4.9,
      price: 'Contact Us',
      image: '/videos/school-partnerships-cover.mp4',
      description: 'Comprehensive STEM programs for schools. We bring our innovation lab experience directly to your educational institution with customized curriculum.',
      features: [
        'Customized curriculum',
        'On-site lab setup',
        'Teacher training programs',
        'Student assessment tools',
        'Progress tracking',
        'Parent engagement',
        'School-wide competitions',
        'Industry connections'
      ],
      instructor: 'STEM Education Specialists',
      badge: 'School'
    },
    // Community Partnerships
    {
      id: 8,
      title: 'Community Partnerships',
      category: 'communities',
      difficulty: 'All Levels',
      duration: 'Flexible',
      students: 70,
      rating: 4.8,
      price: 'Contact Us',
      image: '/videos/community-partnerships.mp4',
      description: 'For churches and other community organizations. We provide STEM education programs that bring families together through learning and innovation.',
      features: [
        'Family-friendly programs',
        'Community center workshops',
        'Weekend sessions',
        'Multi-generational learning',
        'Faith-based integration',
        'Community building',
        'Volunteer opportunities',
        'Local partnerships'
      ],
      instructor: 'STEM Education Specialists',
      badge: 'Community'
    },
    // Industry Visits
    {
      id: 9,
      title: 'Industry Visits & Lab Tours',
      category: 'industry',
      difficulty: 'All Levels',
      duration: 'Monthly',
      students: 55,
      rating: 4.9,
      price: 'Contact Us',
      image: '/videos/Industry-cover.mp4',
      description: 'Monthly industry expert visits and kids also visit industrial technology labs. Real-world exposure to cutting-edge technology and professional environments.',
      features: [
        'Monthly expert visits',
        'Industrial lab tours',
        'Real-world technology exposure',
        'Professional mentorship',
        'Career exploration',
        'Industry networking',
        'Technology demonstrations',
        'Future career guidance'
      ],
      instructor: 'Industry Experts',
      badge: 'Industry'
    }
  ]

  const filteredPrograms = selectedCategory === 'all' 
    ? programs 
    : programs.filter(program => program.category === selectedCategory)

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'All Levels': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getBadgeColor = (badge: string) => {
    switch (badge) {
      case 'Popular': return 'bg-red-500 text-white'
      case 'Trending': return 'bg-blue-500 text-white'
      case 'New': return 'bg-green-500 text-white'
      case 'Featured': return 'bg-purple-500 text-white'
      case 'Advanced': return 'bg-orange-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="gradient-bg py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 px-4">
              <span className="bg-gradient-to-r from-purple-600 to-green-500 bg-clip-text text-transparent">
                Our STEM Programs
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We believe every child has the potential to become a future tech innovator. Our comprehensive STEM programs introduce kids to AI, electronics, robotics, and chip design, sparking curiosity through hands-on exploration. Students advance their skills through our structured program levels, which guide them step-by-step from basic electronics to advanced system thinking and chip design.
            </p>
            <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
              <div className="flex items-center">
                <Award className="h-5 w-5 mr-2 text-primary-600" />
                <span>Expert Instructors</span>
              </div>
              <div className="flex items-center">
                <Users className="h-5 w-5 mr-2 text-primary-600" />
                <span>100+ Students</span>
              </div>
              <div className="flex items-center">
                <Star className="h-5 w-5 mr-2 text-primary-600" />
                <span>4.9/5 Rating</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pre-Register Today Section */}
      <section className="py-8 bg-white border-t-4 border-b-4 border-gradient-to-r from-red-500 to-blue-500 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-red-600 to-blue-600 bg-clip-text text-transparent mb-4 px-6 py-2 leading-tight">
              <span className="block">Pre-Register Today</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
              Secure your child's spot in our innovative STEM programs. Offer Ends soon!
              Grab Early Bird and Year End Festival Season Discount.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/register"
                className="group bg-gradient-to-r from-red-500 to-blue-500 text-white hover:from-red-600 hover:to-blue-600 font-bold text-base px-6 py-3 rounded-xl transition-all duration-300 flex items-center justify-center min-w-[180px] shadow-md hover:shadow-lg transform hover:scale-105"
              >
                Register Now
                <Users className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
              </Link>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-orange-500" />
                  <span>Limited Spots</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => {
              const Icon = category.icon
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{category.name}</span>
                </button>
              )
            })}
          </div>

          {/* Kids-friendly Program Age Guidance card */}
          <div className="mt-8">
            <div className="max-w-5xl mx-auto text-left bg-gradient-to-br from-yellow-50 via-pink-50 to-blue-50 border border-yellow-200/60 rounded-3xl p-6 sm:p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900">Program Age Guidance</h3>
              </div>
              <ul className="grid sm:grid-cols-4 gap-3 mb-3">
                <li className="flex items-center gap-2 bg-white/70 rounded-2xl px-4 py-3 border border-teal-200">
                  <span className="text-gray-800"><span className="font-semibold">Cosmos Juniors</span> (Ages 6 to 8)</span>
                </li>
                <li className="flex items-center gap-2 bg-white/70 rounded-2xl px-4 py-3 border border-yellow-200">
                  <span className="text-gray-800"><span className="font-semibold">Titans</span> (Ages 9 to 15)</span>
                </li>
                <li className="flex items-center gap-2 bg-white/70 rounded-2xl px-4 py-3 border border-pink-200">
                  <span className="text-gray-800"><span className="font-semibold">Warriors</span> (Ages 9 to 15)</span>
                </li>
                <li className="flex items-center gap-2 bg-white/70 rounded-2xl px-4 py-3 border border-blue-200">
                  <span className="text-gray-800"><span className="font-semibold">Lakers</span> (Ages 9 to 15)</span>
                </li>
              </ul>
              <p className="text-sm sm:text-base text-gray-700 bg-white/60 rounded-2xl px-4 py-3 border border-gray-200">
                <span className="font-semibold">Note:</span> For <span className="font-semibold">Lakers</span>, Semi Conductor Chip Design content will not be included for children below age 11.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Structure Section 
      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            
          
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Structured Learning Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each level blends in-person sessions, hands-on projects, and celebration meet-ups that make learning accessible, engaging, and inspiring. Beyond our programs, we continue to mentor and connect with kids through workshops, newsletters, and interactive activities that bring STEM learning into everyday life.
            </p> 
            
          </motion.div>
          
        </div>
      </section>
      */}

      {/* Programs Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPrograms.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`card transition-all duration-300 cursor-pointer group ${
                  expandedProgram === program.id ? 'scale-105 shadow-2xl' : 'hover:scale-105'
                }`}
                onClick={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
              >
                {/* Program Image/Video */}
                <div className="relative mb-4">
                  {program.image.endsWith('.mp4') ? (
                    <video
                      src={program.image}
                      className="w-full h-48 object-cover rounded-lg"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img
                      src={program.image}
                      alt={program.title}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  )}
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getBadgeColor(program.badge)}`}>
                      {program.badge}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(program.difficulty)}`}>
                      {program.difficulty}
                    </span>
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <Play className="h-12 w-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>

                {/* Program Info */}
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                    {program.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {program.description}
                  </p>

                  {/* Program Features */}
                  <div className="flex flex-wrap gap-2">
                    {program.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {feature}
                      </span>
                    ))}
                    {program.features.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        +{program.features.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Program Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{program.duration}</span>
                      </div>
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        <span>{program.students}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Star className="h-4 w-4 mr-1 text-yellow-400 fill-current" />
                      <span>{program.rating}</span>
                    </div>
                  </div>

                  {/* Instructor */}
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Instructor:</span> {program.instructor}
                  </div>

                  {/* Expanded Content */}
                  {expandedProgram === program.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4 pt-4 border-t"
                    >
                      {/* Detailed Description */}
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Program Overview</h4>
                        <p className="text-sm text-gray-600">{program.detailedDescription}</p>
                      </div>

                      {/* Curriculum */}
                      {program.curriculum && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Curriculum</h4>
                          <div className="space-y-2">
                            {program.curriculum.map((module, idx) => (
                              <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex justify-between items-center mb-2">
                                  <span className="font-medium text-sm">{module.module}</span>
                                  {('duration' in module) && (
                                    <span className="text-xs text-gray-500">{(module as any).duration}</span>
                                  )}
                                </div>
                                {'theme' in module && module.theme && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-1 rounded">
                                      Theme: {module.theme}
                                    </span>
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1">
                                  {module.topics.map((topic, topicIdx) => (
                                    <span key={topicIdx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                      {topic}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Hands-on Projects */}
                      {(program as any).handsOnProjects && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Hands-on Projects</h4>
                          <div className="space-y-2">
                            {(program as any).handsOnProjects.map((project: any, idx: number) => (
                              <div key={idx} className="bg-green-50 p-3 rounded-lg">
                                <div className="flex justify-between items-start mb-2">
                                  <h5 className="font-medium text-sm text-gray-900">{project.title}</h5>
                                  <span className="text-xs text-gray-500">{project.duration}</span>
                                </div>
                                <p className="text-xs text-gray-600 mb-2">{project.description}</p>
                                <div className="flex flex-wrap gap-1">
                                  {project.skills.map((skill: any, skillIdx: number) => (
                                    <span key={skillIdx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Learning Outcomes */}
                      {program.outcomes && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Learning Outcomes</h4>
                          <ul className="space-y-1">
                            {program.outcomes.map((outcome, idx) => (
                              <li key={idx} className="flex items-center text-sm text-gray-600">
                                <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" />
                                <span>{outcome}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* Price and CTA */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-2xl font-bold text-primary-600">
                      {program.price}
                    </div>
                    <Link 
                      to="/register" 
                      className="btn-primary text-sm px-4 py-2"
                    >
                      {expandedProgram === program.id ? 'Explore More' : 'Enroll Now'}
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Load More (temporarily disabled)
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <button className="btn-outline px-8 py-3">
              Load More Programs
            </button>
          </motion.div>
          */}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Can't Find What You're Looking For?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              We're always adding new programs and projects. Let us know what you'd like to learn!
            </p>
            <Link to="/contact" className="bg-white text-primary-600 hover:bg-gray-100 font-medium py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl">
              Request a Program
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default ProgramsPage
